import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { connect, createLocalTracks } from "twilio-video";
import { api } from "../lib/api";
import { Mic, MicOff, Video, VideoOff, PhoneOff } from "lucide-react";
import { toast } from "sonner";

export default function CallRoom() {
  const { uid } = useParams();
  const [params] = useSearchParams();
  const callType = params.get("type") || "video";
  const nav = useNavigate();
  const [room, setRoom] = useState(null);
  const [localTracks, setLocalTracks] = useState([]);
  const [remoteParticipants, setRemoteParticipants] = useState([]);
  const [muted, setMuted] = useState(false);
  const [camOff, setCamOff] = useState(false);
  const [other, setOther] = useState(null);
  const [status, setStatus] = useState("connecting");
  const localRef = useRef(null);
  const remoteRef = useRef(null);

  // deterministic 1-on-1 room: sort both UIDs
  useEffect(() => {
    api.get(`/providers/${uid}`).then(({ data }) => setOther(data)).catch(() => {});
  }, [uid]);

  useEffect(() => {
    let activeRoom = null;
    let tracks = [];
    const join = async () => {
      try {
        const { data: me } = await api.get("/auth/me");
        const myId = me.user.id;
        const roomName = `dm_${[myId, uid].sort().join("_")}`;
        const { data: tk } = await api.post("/video/token", {
          roomName,
          role: "caller",
        });

        tracks = await createLocalTracks({
          audio: true,
          video: callType === "video" ? { width: 640 } : false,
        });
        setLocalTracks(tracks);

        // Attach local
        if (callType === "video" && localRef.current) {
          const vt = tracks.find((t) => t.kind === "video");
          if (vt) localRef.current.appendChild(vt.attach());
        }

        activeRoom = await connect(tk.token, {
          name: roomName,
          tracks,
          dominantSpeaker: false,
        });
        setRoom(activeRoom);
        setStatus("connected");

        const handleParticipant = (participant) => {
          setRemoteParticipants((prev) => [...prev.filter((p) => p.sid !== participant.sid), participant]);
          participant.tracks.forEach((pub) => {
            if (pub.isSubscribed && pub.track) attachRemote(pub.track);
          });
          participant.on("trackSubscribed", attachRemote);
          participant.on("trackUnsubscribed", (track) => {
            track.detach().forEach((el) => el.remove());
          });
        };
        activeRoom.participants.forEach(handleParticipant);
        activeRoom.on("participantConnected", handleParticipant);
        activeRoom.on("participantDisconnected", (p) => {
          setRemoteParticipants((prev) => prev.filter((x) => x.sid !== p.sid));
        });
      } catch (e) {
        console.error(e);
        const msg = String(e?.message || e?.code || "");
        if (msg.includes("20101") || msg.toLowerCase().includes("access token") || msg.toLowerCase().includes("unauthorized") || msg.includes("401")) {
          toast.error("Video calling isn't activated yet. Admin: enable client-initiated room creation in Twilio Console → Programmable Video → Manage → Room Settings.");
        } else {
          toast.error(e.message || "Could not join call");
        }
        setStatus("error");
      }
    };
    const attachRemote = (track) => {
      if (remoteRef.current) {
        const el = track.attach();
        if (track.kind === "video") {
          el.style.width = "100%";
          el.style.height = "100%";
          el.style.objectFit = "cover";
        }
        remoteRef.current.appendChild(el);
      }
    };
    join();
    return () => {
      tracks.forEach((t) => t.stop());
      if (activeRoom) activeRoom.disconnect();
    };
    // eslint-disable-next-line
  }, [uid, callType]);

  const toggleMute = () => {
    setMuted(!muted);
    localTracks
      .filter((t) => t.kind === "audio")
      .forEach((t) => (muted ? t.enable() : t.disable()));
  };
  const toggleCam = () => {
    setCamOff(!camOff);
    localTracks
      .filter((t) => t.kind === "video")
      .forEach((t) => (camOff ? t.enable() : t.disable()));
  };
  const end = () => {
    localTracks.forEach((t) => t.stop());
    if (room) room.disconnect();
    nav(-1);
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="border-b border-secondary/15 p-4 flex justify-between items-center">
        <div>
          <div className="label-mystic">{callType === "video" ? "Video Consultation" : "Voice Consultation"}</div>
          <div className="font-serif text-2xl">{other?.businessName || "Connecting..."}</div>
        </div>
        <span className={`text-xs px-3 py-1 border uppercase tracking-wider ${
          status === "connected" ? "bg-primary/20 text-primary border-primary/40 pulse-ember"
            : status === "error" ? "bg-destructive/20 text-destructive border-destructive/40"
            : "bg-secondary/10 text-secondary border-secondary/30"
        }`} data-testid="call-status">● {status}</span>
      </div>

      <div className="flex-1 flex items-center justify-center p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full max-w-5xl">
          <div ref={remoteRef} className="relative aspect-video bg-card border border-secondary/15 flex items-center justify-center overflow-hidden" data-testid="call-remote">
            {remoteParticipants.length === 0 && (
              <div className="text-center">
                <div className="w-32 h-32 mx-auto rounded-full bg-secondary/10 border border-secondary/30 flex items-center justify-center font-serif text-6xl text-secondary mb-4">
                  {other?.businessName?.[0] || "?"}
                </div>
                <div className="font-serif text-xl">{other?.businessName}</div>
                <div className="label-mystic mt-1">Awaiting them to join</div>
              </div>
            )}
          </div>
          <div className="relative aspect-video bg-black border border-secondary/15 overflow-hidden" data-testid="call-local">
            <div ref={localRef} className="w-full h-full [&>video]:w-full [&>video]:h-full [&>video]:object-cover" />
            {(callType !== "video" || camOff) && (
              <div className="absolute inset-0 flex items-center justify-center text-foreground/60 bg-black/80">Camera off</div>
            )}
            <div className="absolute bottom-2 left-2 label-mystic bg-black/60 px-2 py-1">You</div>
          </div>
        </div>
      </div>

      <div className="p-6 flex justify-center gap-4">
        <button onClick={toggleMute} className="w-14 h-14 rounded-full bg-card border border-secondary/30 hover:bg-secondary/10 flex items-center justify-center" data-testid="call-mute-btn">
          {muted ? <MicOff size={20} strokeWidth={1.25} /> : <Mic size={20} strokeWidth={1.25} />}
        </button>
        {callType === "video" && (
          <button onClick={toggleCam} className="w-14 h-14 rounded-full bg-card border border-secondary/30 hover:bg-secondary/10 flex items-center justify-center" data-testid="call-cam-btn">
            {camOff ? <VideoOff size={20} strokeWidth={1.25} /> : <Video size={20} strokeWidth={1.25} />}
          </button>
        )}
        <button onClick={end} className="w-14 h-14 rounded-full bg-destructive hover:bg-destructive/80 flex items-center justify-center" data-testid="call-end-btn">
          <PhoneOff size={20} strokeWidth={1.25} />
        </button>
      </div>
    </div>
  );
}
